<template>
  <b-card>
    <b-button
      id="default-filter-new-btn"
      variant="primary"
      type="button"
      size="sm"
      class="mb-2 ml-2"
      @click="createDepartmentRuleSets($route.params.id)"
    >
      <feather-icon icon="PlusIcon" size="15" />
      <span>{{ t('Create Department Rule Set') }}</span>
      <b-tooltip
        triggers="hover"
        target="default-filter-new-btn"
        :title="t('Add New Price Packages')"
        :delay="{ show: 0, hide: 50 }"
      />
    </b-button>
    <b-card-body>
      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'name'">
            {{ props.row.name || '-' }}
          </span>
          <span v-else-if="props.column.field === 'value'">
            {{ props.row.value || '-' }}
          </span>
          <span v-else-if="props.column.field === 'key'">
            {{ props.row.key || '-' }}
          </span>
          <span v-else-if="props.column.field === 'type'">
            {{ departmentRuleType(props.row.type || '-') }}
          </span>
          <span v-else-if="props.column.field === 'condition'">
            {{ departmentRuleConditions(props.row.condition || '-') }}
          </span>
          <span v-if="props.column.field === 'status'">
            <b-badge pill class="text-white" :variant="props.row.status ? 'success' : 'danger'">
              {{ props.row.status ? t('Active') : t('Passive') }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="editDepartmentRule(props.row.id)"
              >
                <feather-icon icon="Edit2Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="$t('Edit Department')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>

              <b-button
                :id="`delete-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="deleteDepartmentRule(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-button-${props.row.id}`"
                  :title="$t('Delete Department')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BBadge, BCard, BCardBody, BTooltip, BButton } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import store from '@/store';

export default {
  components: {
    VueGoodTable,
    BBadge,
    BCard,
    BCardBody,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        name: '',
        address: '',
        postalCode: '',
        phoneNumber: '',
        email: ''
      },
      defaultFilterValues: {
        name: '',
        address: '',
        postalCode: '',
        phoneNumber: '',
        email: ''
      }
    };
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Name'),
          field: 'name'
        },
        {
          label: this.$t('Type'),
          field: 'type'
        },
        {
          label: this.$t('Condition'),
          field: 'condition'
        },
        {
          label: this.$t('Key'),
          field: 'key'
        },
        {
          label: this.$t('Value'),
          field: 'value'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  async created() {
    await this.getDepartmentRuleCondition().then(response => {
      this.conditionOptions = response.map(x => ({
        text: x.info,
        value: x.value
      }));
    });
    await this.getDepartmentRuleKey().then(response => {
      this.keyOptions = response.map(x => ({
        text: x.name,
        value: x.key
      }));
    });
    await this.getDepartmentRuleType().then(response => {
      this.typeOptions = response.map(x => ({
        text: x.info,
        value: x.value
      }));
    });
    this.loadItems();
  },

  methods: {
    createDepartmentRuleSets(id) {
      this.$router.push({ name: 'departments_rule_create', params: { id } });
    },
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    // load items is what brings back the rows from server
    loadItems() {
      let requestData = this.filter;
      const { id } = this.$route.params;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      if (
        requestData.dateFrom &&
        requestData.dateTo &&
        new Date(requestData.dateFrom) > new Date(requestData.dateTo)
      ) {
        this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'));
        return;
      }

      this.isLoading = true;
      useService.departmentService
        .getDepartmentRuleSets(id)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setFilter(data) {
      this.filterValues = data || this.defaultFilterValues;
      this.filter.page = 1;
      this.loadItems();
    },
    editDepartmentRule(ruleId) {
      const { id } = this.$route.params;
      this.$router.push({ name: 'edit_department_rule', params: { id, ruleId } });
    },
    deleteDepartmentRule(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Department Rule Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await useService.departmentService.deleteDepartmentRule(id).then(response => {
            this.showSuccessMessage(
              `${response.message || this.$t('Delete Department Rule Success Message')}`
            );

            const index = this.rows.findIndex(item => item.id === id);
            this.rows.splice(index, 1);
          });
        }
      });
    },
    departmentRuleType(type) {
      let name = '-';
      const item = this.typeOptions.find(x => x.value === type);

      if (item) {
        name = item.text;
      }

      return name;
    },
    departmentRuleConditions(type) {
      let name = '-';
      const item = this.conditionOptions.find(x => x.value === type);

      if (item) {
        name = item.text;
      }

      return name;
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
